// Snake & Ladder board math helpers (frontend mirror of the server's
// board.js). Pure, no React, no DOM — just block ↔ pixel mapping for
// the SVG renderer.
//
// The board is a 10×10 grid numbered 1-100 in standard "boustrophedon"
// zigzag: row 0 (bottom) goes 1→10 left-to-right, row 1 goes 11→20
// right-to-left, etc. We keep all coordinates in a normalised 0..1000
// SVG viewBox so the React component can scale freely with the
// available viewport.

export const BOARD_VIEW = 1000;          // SVG viewBox edge
export const CELL_SIZE  = BOARD_VIEW / 10; // 100

/**
 * Convert a 1-100 block to the centre {x, y} pixel coordinate inside
 * the 1000×1000 SVG viewBox. Block 1 sits at the bottom-left; block
 * 100 at the top-left (because row 9 goes right-to-left).
 */
export function centerOf(block: number): { x: number; y: number } {
  if (block <= 0 || block > 100) return { x: -100, y: -100 };
  const rowFromBottom = Math.floor((block - 1) / 10);     // 0..9
  const isLeftToRight = rowFromBottom % 2 === 0;
  const col = isLeftToRight
    ? (block - 1) % 10
    : 9 - ((block - 1) % 10);
  const x = col * CELL_SIZE + CELL_SIZE / 2;
  const y = BOARD_VIEW - rowFromBottom * CELL_SIZE - CELL_SIZE / 2;
  return { x, y };
}

/** Top-left {x, y} of the cell (used to position the number label). */
export function topLeftOf(block: number): { x: number; y: number } {
  const c = centerOf(block);
  return { x: c.x - CELL_SIZE / 2, y: c.y - CELL_SIZE / 2 };
}

/**
 * Cubic Bezier path for a snake from `head` block to `tail` block. The
 * curve sweeps outward via two control points so adjacent snakes don't
 * sit on top of each other visually. `kind` decides the curvature
 * pattern (alternating S/C shapes for variety).
 */
export function snakeCurvePath(
  head: number,
  tail: number,
  kind: 0 | 1 | 2 | 3 = 0,
): string {
  const a = centerOf(head);
  const b = centerOf(tail);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distance = Math.hypot(dx, dy) || 1;
  const perpX = -dy / distance;
  const perpY =  dx / distance;
  const strength = Math.min(distance * 0.55, 200);
  let off1: number, off2: number;
  switch (kind) {
    case 0: off1 = +strength; off2 = +strength; break;       // C
    case 1: off1 = -strength; off2 = -strength; break;       // reverse C
    case 2: off1 = +strength; off2 = -strength; break;       // S
    default: off1 = -strength; off2 = +strength;             // reverse S
  }
  const cp1x = a.x + dx * 0.33 + perpX * off1;
  const cp1y = a.y + dy * 0.33 + perpY * off1;
  const cp2x = a.x + dx * 0.66 + perpX * off2;
  const cp2y = a.y + dy * 0.66 + perpY * off2;
  return `M ${a.x} ${a.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${b.x} ${b.y}`;
}

/**
 * Sample the SAME cubic-bezier a snake is drawn with into an ordered
 * list of {x,y} points (head → tail). The move-animation walks a token
 * through these so it slithers DOWN the snake's body to the tail —
 * ported from the reference's animateTokenAlongPath.
 */
export function snakePathPoints(
  head: number,
  tail: number,
  kind: 0 | 1 | 2 | 3 = 0,
  samples = 64,
): Array<{ x: number; y: number }> {
  const a = centerOf(head);
  const b = centerOf(tail);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distance = Math.hypot(dx, dy) || 1;
  const perpX = -dy / distance;
  const perpY =  dx / distance;
  const strength = Math.min(distance * 0.55, 200);
  let off1: number, off2: number;
  switch (kind) {
    case 0: off1 = +strength; off2 = +strength; break;
    case 1: off1 = -strength; off2 = -strength; break;
    case 2: off1 = +strength; off2 = -strength; break;
    default: off1 = -strength; off2 = +strength;
  }
  const cp1x = a.x + dx * 0.33 + perpX * off1;
  const cp1y = a.y + dy * 0.33 + perpY * off1;
  const cp2x = a.x + dx * 0.66 + perpX * off2;
  const cp2y = a.y + dy * 0.66 + perpY * off2;
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const mt = 1 - t;
    const x = mt * mt * mt * a.x + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * b.x;
    const y = mt * mt * mt * a.y + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * b.y;
    pts.push({ x, y });
  }
  return pts;
}

/**
 * Deterministic curve-shape selector for a snake, derived ONLY from its
 * head+tail squares. Both the renderer (Board.tsx <SnakeGraphic>) and the
 * snake-bite animation (Page.tsx) call this so the path a bitten token
 * walks is GUARANTEED to be the same bezier the snake body was drawn with
 * — no array-index lookup that could miss (the old code fell back to
 * kind 0 when findIndex returned -1, making the token follow the wrong
 * curve for some snakes).
 */
export function snakeKind(head: number, tail: number): 0 | 1 | 2 | 3 {
  return ((((head * 3 + tail) % 4) + 4) % 4) as 0 | 1 | 2 | 3;
}

export type SnakeGeometry = {
  points: Array<{ x: number; y: number }>;
  halfWidths: number[];
  angles: number[];               // tangent angle (rad) at each point
  bodyPath: string;               // filled tapering body outline
  outlinePath: string;            // slightly fatter dark outline
  headPt: { x: number; y: number };
  headAngle: number;              // direction the head faces (rad)
};

// Reference body profile: thin neck, fat mid-body, whip-thin tail.
// t=0 head → t=1 tail.  (Ported from the grandchild's drawSnakes.)
function snakeProfile(t: number): number {
  return Math.sin(t * Math.PI) * 0.4 + Math.pow(1 - t, 0.5) * 0.6;
}

/**
 * Full tapering-body geometry for a snake: a filled polygon that is
 * fat in the middle and tapers to a point at the tail, plus per-point
 * tangent angles (for placing rotated scale/stripe patterns) and the
 * head orientation. maxRadius is the half-width at the fattest point.
 */
export function snakeGeometry(
  head: number,
  tail: number,
  kind: 0 | 1 | 2 | 3 = 0,
  maxRadius = 13,
  samples = 64,
): SnakeGeometry {
  const pts = snakePathPoints(head, tail, kind, samples);
  const N = pts.length - 1;
  const halfWidths: number[] = [];
  const angles: number[] = [];
  const normals: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= N; i++) {
    const prev = pts[Math.max(0, i - 1)];
    const next = pts[Math.min(N, i + 1)];
    let tx = next.x - prev.x, ty = next.y - prev.y;
    const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl;
    angles.push(Math.atan2(ty, tx));
    normals.push({ x: -ty, y: tx });
    halfWidths.push(maxRadius * snakeProfile(i / N));
  }
  const edge = (extra: number) => {
    const L: string[] = [];
    const R: string[] = [];
    for (let i = 0; i <= N; i++) {
      const w = halfWidths[i] + extra;
      const n = normals[i];
      L.push(`${(pts[i].x + n.x * w).toFixed(1)} ${(pts[i].y + n.y * w).toFixed(1)}`);
      R.push(`${(pts[i].x - n.x * w).toFixed(1)} ${(pts[i].y - n.y * w).toFixed(1)}`);
    }
    return `M ${L[0]} ${L.slice(1).map((p) => 'L ' + p).join(' ')} ${R.reverse().map((p) => 'L ' + p).join(' ')} Z`;
  };
  const neck = pts[Math.floor(N * 0.05)];
  return {
    points: pts,
    halfWidths,
    angles,
    bodyPath: edge(0),
    outlinePath: edge(3),
    headPt: pts[0],
    headAngle: Math.atan2(pts[0].y - neck.y, pts[0].x - neck.x),
  };
}

/** Straight line for ladders (with a tiny perpendicular offset for
 *  the two rails). Returns the centre line; callers draw two parallel
 *  rails by translating by ±(rail width / 2) along the perpendicular. */
export function ladderLine(
  base: number,
  top: number,
): { x1: number; y1: number; x2: number; y2: number; angleRad: number; length: number } {
  const a = centerOf(base);
  const b = centerOf(top);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  const angleRad = Math.atan2(dy, dx);
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, angleRad, length };
}
